import { useState, useContext } from 'react';
import { toast } from '../lib/toast.js';
import postRegister from '../functions/API/postRegister.js';
import PasswordStrengthChecker from '../components/PasswordStrengthChecker.js';
import moment from 'moment';
import GlobalContext from "../context/GlobalContext.js";
import { darkenColor } from '../functions/colorUtils.js';
import {
    UserPlus, X, ArrowRight, ArrowLeft, Eye, EyeOff,
} from 'lucide-react';

const STEPS = [
    { key: 'firstname', question: "What's your first name?", description: 'Enter your legal first name as it appears on your agreement', placeholder: 'First name', type: 'text' },
    { key: 'lastname', question: "What's your last name?", description: 'Enter your legal last name as it appears on your agreement', placeholder: 'Last name', type: 'text' },
    { key: 'dob', question: 'What is your date of birth?', description: 'This is used to verify your identity', placeholder: 'Date of birth', type: 'date' },
    { key: 'agreement', question: 'What is your agreement reference?', description: 'You can find this on your tenancy documents', placeholder: 'Agreement reference', type: 'text' },
    { key: 'email', question: 'What is your email address?', description: 'This will be used to log in to your account', placeholder: 'Email address', type: 'email' },
    { key: 'password', question: 'Create a password', description: 'Choose a strong password to secure your account', placeholder: 'Password', type: 'password' },
];

export default function Register() {
    const { settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const primaryDark = darkenColor(primaryColor);

    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState({
        firstname: '', lastname: '', dob: '', agreement: '', email: '', password: '', confirmpassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [passwordSecure, setPasswordSecure] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const currentStep = STEPS[step];
    const isLastStep = step === STEPS.length - 1;
    const value = formData[currentStep.key] || '';

    function updateField(key, val) {
        setFormData(prev => ({ ...prev, [key]: val }));
    }

    function next() {
        if (step === 0 && !formData.firstname.trim()) { toast.error('Please enter your first name'); return; }
        if (step === 1 && !formData.lastname.trim()) { toast.error('Please enter your last name'); return; }
        if (step === 2 && !formData.dob) { toast.error('Please enter your date of birth'); return; }
        if (step === 3 && !formData.agreement.trim()) { toast.error('Please enter your agreement reference'); return; }
        if (step === 4 && !formData.email.trim()) { toast.error('Please enter your email address'); return; }
        if (isLastStep) { submitRegistration(); return; }
        setStep(s => s + 1);
    }

    function back() {
        if (step > 0) setStep(s => s - 1);
    }

    async function submitRegistration() {
        if (!passwordSecure) { toast.error('Your password does not meet the minimum requirements'); return; }
        if (formData.password !== formData.confirmpassword) { toast.error("Your passwords don't match"); return; }
        setSubmitting(true);
        const dob = moment(formData.dob).format('YYYY-MM-DD');
        const response = await postRegister(formData.firstname, formData.lastname, dob, formData.agreement, formData.email, formData.password, formData.confirmpassword);
        if (response.response?.ok) {
            toast.success('Account created! Redirecting to login...');
            setTimeout(() => { window.location.href = '/login'; }, 3000);
        } else {
            toast.error("Something didn't add up, please check your details and try again.");
            setStep(0);
            setSubmitting(false);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') { e.preventDefault(); next(); }
    }

    const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow';

    // Shared gradient header content
    function GradientContent({ className }) {
        return (
            <div className={className}>
                {/* Exit button */}
                <a
                    href="/login"
                    className="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors mb-5"
                >
                    <X size={16} strokeWidth={2.5} />
                    Exit
                </a>

                {/* Progress bar */}
                <div className="flex gap-1.5 mb-8">
                    {STEPS.map((_, i) => (
                        <div
                            key={i}
                            className="flex-1 h-1 rounded-full transition-colors duration-300"
                            style={{ backgroundColor: i <= step ? '#fff' : 'rgba(255,255,255,0.25)' }}
                        />
                    ))}
                </div>

                {/* Title */}
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <UserPlus size={22} className="text-white" strokeWidth={1.8} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">Create account</h1>
                </div>

                {/* Desktop: step indicator */}
                <p className="hidden lg:block mt-6 text-white/60 text-sm">
                    Step {step + 1} of {STEPS.length}
                </p>
            </div>
        );
    }

    // Shared form content
    const formContent = (
        <>
            {/* Question */}
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">{currentStep.question}</h2>
            <p className="text-sm text-gray-500 mb-6">{currentStep.description}</p>

            {/* Input(s) */}
            {currentStep.key === 'password' ? (
                <div className="space-y-3">
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={e => updateField('password', e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Password"
                            className={`${inputClass} pr-12`}
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <div className="relative">
                        <input
                            type={showConfirm ? 'text' : 'password'}
                            value={formData.confirmpassword}
                            onChange={e => updateField('confirmpassword', e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Confirm password"
                            className={`${inputClass} pr-12`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <PasswordStrengthChecker password={formData.password} passwordSecure={setPasswordSecure} />
                </div>
            ) : (
                <input
                    type={currentStep.type}
                    value={value}
                    onChange={e => updateField(currentStep.key, e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={currentStep.placeholder}
                    className={inputClass}
                    autoFocus
                />
            )}

            {/* Buttons */}
            <div className="mt-6 flex gap-3">
                {step > 0 && (
                    <button
                        onClick={back}
                        className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-sm font-semibold border-2 transition-opacity hover:opacity-80"
                        style={{ borderColor: primaryColor, color: primaryColor }}
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>
                )}
                <button
                    onClick={next}
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                >
                    {submitting ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : isLastStep ? (
                        'Register'
                    ) : (
                        <>Next <ArrowRight size={16} /></>
                    )}
                </button>
            </div>

            {/* Login link */}
            <p className="mt-8 text-center text-sm text-gray-500">
                Already have an account?{' '}
                <a href="/login" className="font-semibold hover:opacity-80" style={{ color: primaryColor }}>
                    Sign in
                </a>
            </p>
        </>
    );

    return (
        <>
            {/* ── Mobile layout ────────────────────────────────────────────── */}
            <div className="lg:hidden min-h-screen flex flex-col" style={{ backgroundColor: '#F2F2F7' }}>
                {/* Gradient header */}
                <div
                    className="w-full flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}
                >
                    <GradientContent className="px-5 pt-5 pb-12" />
                </div>

                {/* Content card */}
                <div className="flex-1 -mt-6 relative z-10">
                    <div className="bg-[#F2F2F7] rounded-t-[1.5rem] min-h-full">
                        <div className="px-5 pt-7 pb-12">
                            {formContent}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Desktop layout ───────────────────────────────────────────── */}
            <div className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#F2F2F7' }}>
                {/* Left half — gradient */}
                <div
                    className="w-1/2 flex items-center justify-center p-12"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}
                >
                    <GradientContent className="w-full max-w-md" />
                </div>

                {/* Right half — form */}
                <div className="w-1/2 flex items-center justify-center p-12">
                    <div className="w-full max-w-md">
                        {formContent}
                    </div>
                </div>
            </div>
        </>
    );
}
